// Club events, resolved against the campus map: where a pin goes, and what
// "Directions" walks to.
//
// The events themselves come from the host — this folder never fetches them and
// does no date arithmetic beyond comparing the millisecond timestamps the host
// puts on them. What it does own is the two questions only the map can answer:
// which point on the map an event is at, and which nav node the router should
// aim for. Those are the same two questions the Nearby sheet answers for a
// place, and for the same reason they are two: what is drawn and what is walked
// to are different points.
//
// The event shape this expects (everything optional but `id`):
//   { id, name, host, logo, buildingSlug, buildingName, blocks,
//     room: { roomNumber, nodeIds: [], point: {lat,lng}, floor },
//     startsAt, endsAt,        — ms since epoch, or null for "date to be announced"
//     when: { text, tone },    — the short line printed on the pin
//     whenLong, where, url,
//     tags: [{ key, label, title? }],   — badges, already worded by the host
//     ticket: { url, label },           — the "Buy tickets: $10" button
//     offsite: { url } }                — not on campus: a link out, no route
//
// The last three are the host's whole vocabulary for a club's tags, and they
// arrive DECIDED. Whether "Engineering students only" should be printed or used
// to hide the event, how a price is written, and what counts as off campus are
// questions about one university's students and one site's data — the answers
// do not belong in a folder that is meant to drop into another app unchanged.
// This file's rule holds: it knows where things are, and nothing else.

/** How far ahead the map pins look. A week: "what can I still go to". */
export const PIN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Average of every vertex in a ring — a good-enough centroid. */
function ringCentroid(ring) {
  let lat = 0, lng = 0, n = 0;
  for (const p of ring || []) {
    if (p?.lat == null || p?.lng == null) continue;
    lat += p.lat; lng += p.lng; n++;
  }
  return n ? { lat: lat / n, lng: lng / n } : null;
}

/** Bounding-box area of a ring, for picking the biggest of several. */
function ringSpan(ring) {
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const p of ring || []) {
    if (p?.lat == null || p?.lng == null) continue;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  return minLat === Infinity ? 0 : (maxLat - minLat) * (maxLng - minLng);
}

/**
 * Where an event's pin sits, in three fallbacks.
 *
 *  1. The point the club dropped, when it pinned a spot rather than naming a
 *     room — an atrium, a concourse. That is the truest answer there is: it is
 *     literally where they said the event is.
 *  2. The room's own polygon, when the building's interior has been loaded.
 *     Interiors are fetched lazily, so this usually misses on a fresh page and
 *     that is fine — the pin is placed on the building either way, and the
 *     difference at campus zoom is a few pixels.
 *  3. The building's footprint. `b.outlines` holds one ring PER FLOOR, and an
 *     upper floor can be a single wing, so the largest is taken rather than the
 *     first — averaging all of them drags the pin toward whichever end of the
 *     building happens to have the most storeys.
 */
export function eventPosition(mapData, event) {
  const lat = Number(event?.room?.point?.lat);
  const lng = Number(event?.room?.point?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };

  const b = mapData?.buildings?.find(x => x.slug === event?.buildingSlug);
  if (!b) return null;

  const want = String(event?.room?.roomNumber || '').toLowerCase();
  if (want) {
    for (const polys of b.polygonsByFloor.values()) {
      const hit = polys.find(p => String(p.roomNumber).toLowerCase() === want);
      const c = hit && ringCentroid(hit.ring);
      if (c) return c;
    }
  }

  let best = null, bestSpan = -1;
  for (const ring of b.outlines || []) {
    const span = ringSpan(ring);
    if (span > bestSpan) { bestSpan = span; best = ring; }
  }
  return best ? ringCentroid(best) : null;
}

/**
 * What "Directions" routes to: a roomIndex-shaped entry the normal destination
 * flow can take, or null when nothing on the walking network matches.
 *
 * The order matters and each step is a different kind of event:
 *
 *  · a named room resolves through the routing index, which already carries
 *    every door of it;
 *  · ⚠️ a PINNED spot has no room number, so that lookup finds nothing. Its
 *    stored `nodeIds` is the nav node it snapped to when it was placed, and
 *    without this step it would fall through to the building's front door —
 *    walking people to the entrance of a building whose atrium was the point;
 *  · anything else settles on the building itself, and the router picks the
 *    nearest entrance, which is what it would do anyway.
 */
export function eventDestination(mapData, event) {
  if (!mapData || !event?.buildingSlug) return null;
  const b = mapData.buildings?.find(x => x.slug === event.buildingSlug);
  const buildingName = event.buildingName || b?.name || event.buildingSlug;

  const want = String(event.room?.roomNumber || '').toLowerCase();
  if (want) {
    const room = (mapData.roomIndex || []).find(
      r => r.building === event.buildingSlug && String(r.roomNumber).toLowerCase() === want);
    if (room) return room;
  }

  const pinned = (event.room?.nodeIds || []).find(id => mapData.nodes.has(id));
  if (pinned) {
    return {
      building: event.buildingSlug,
      buildingName,
      // The label the route pill prints. The event's own name, because that is
      // what the person tapped — "Drake Centre exit 3" would be technically
      // truer and useless.
      roomNumber: event.room?.roomNumber || event.name || buildingName,
      nodeId: pinned,
      nodeIds: (event.room.nodeIds || []).filter(id => mapData.nodes.has(id)),
    };
  }

  const exits = [];
  for (const n of mapData.nodes.values()) {
    if (n.building === event.buildingSlug && n.type === 'exit') exits.push(n.id);
  }
  if (!exits.length) return null;
  return {
    building: event.buildingSlug,
    buildingName,
    roomNumber: buildingName,
    nodeId: exits[0],
    nodeIds: exits,
  };
}

/**
 * The events that earn a pin: happening within the window and not yet over.
 *
 * An event with no date announced is deliberately absent from the map and
 * present in the calendar — a pin says "go here, around now", which is the one
 * thing an undated event cannot promise.
 */
export function pinnableEvents(events, now = Date.now(), window = PIN_WINDOW_MS) {
  return (events || []).filter(e =>
    Number.isFinite(e?.startsAt)
    && e.startsAt <= now + window
    && (!Number.isFinite(e?.endsAt) || e.endsAt >= now));
}
